// AICA Switch Component - Meridian Dark Design System
// Based on Radix UI Switch primitive
// Executive style: iOS-inspired with teal active state and gold accent

import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn } from '@/lib/utils';

export interface SwitchProps extends React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
> {
  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Error state
   */
  error?: boolean;
  /**
   * Show a gold accent when checked
   */
  accent?: boolean;
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(({ className, size = 'md', error, accent, ...props }, ref) => {
  const sizes = {
    sm: {
      root: 'h-5 w-9',
      thumb: 'h-4 w-4 data-[state=checked]:translate-x-4',
    },
    md: {
      root: 'h-6 w-11',
      thumb: 'h-5 w-5 data-[state=checked]:translate-x-5',
    },
    lg: {
      root: 'h-7 w-14',
      thumb: 'h-6 w-6 data-[state=checked]:translate-x-7',
    },
  };

  return (
    <SwitchPrimitives.Root
      className={cn(
        // Base styles - Meridian Dark
        'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
        'bg-bg-subtle', // Subtle gray when unchecked

        // Focus states - Meridian teal ring
        'focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-primary/30',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-bg',

        // Checked state - Teal background
        'data-[state=checked]:bg-primary',

        // Hover states with glow
        'hover:bg-bg-accent hover:shadow-glow-subtle',
        'data-[state=checked]:hover:bg-primary-hover data-[state=checked]:hover:shadow-glow-primary',

        // Error state with danger glow
        error && [
          'bg-danger-dim',
          'data-[state=checked]:bg-danger',
          'focus-visible:ring-danger/30',
          'hover:shadow-glow-error',
        ],

        // Disabled state
        'disabled:cursor-not-allowed',
        'disabled:opacity-50',
        'disabled:bg-bg-subtle',

        // Transitions
        'transition-all duration-150 ease-standard',
        'motion-reduce:transition-none',

        // Size variant
        sizes[size].root,

        className
      )}
      ref={ref}
      aria-invalid={error ? 'true' : undefined}
      {...props}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          // Thumb styles
          'pointer-events-none block rounded-full',
          'bg-bg shadow-lg', // Always white thumb (iOS-style, theme-independent)
          'ring-0',

          // Accent mode - gold thumb when checked
          accent && 'data-[state=checked]:bg-accent',

          // Error state
          error && 'bg-danger',

          // Transitions
          'transition-all duration-150 ease-standard',
          'motion-reduce:transition-none',

          // Animation - smooth slide
          'data-[state=unchecked]:translate-x-0.5',

          // Size variant with translation
          sizes[size].thumb
        )}
      />
    </SwitchPrimitives.Root>
  );
});

Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
